import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  OnePieceCard,
} from '../../types/onepiece';
import { PokemonCard } from '../../types/pokemon';
import {
  useOnePieceCardOwnership,
  useUpdateOnePieceCardOwnership,
  useToggleOnePieceCardOwnership,
} from '../../hooks/useOnePieceCardOwnership';
import {
  useCardOwnership,
  useUpdateCardOwnership,
  useToggleCardOwnership,
} from '../../hooks/useCardOwnership';
import {
  useToggleWishlist,
  useToggleFavorite,
} from '../../hooks/useUserIntents';
import { EnrichedOwnership } from '../../hooks/useEnrichedCollection';
import type { CardOwnershipData } from '../../components/CardOwnershipForm/CardOwnershipForm';
import CardDetailView from '../../components/CardDetailView/CardDetailView';
import { tcgDataService } from '../../services/tcgDataService';
import { findOnePieceCard, transformApiCardToOnePieceCard } from '../../utils/onepieceData';

const CollectionCardDetail: React.FC = () => {
  const { cardId, setId } = useParams<{ cardId: string; setId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [card, setCard] = useState<OnePieceCard | PokemonCard | null>(null);

  // Get the enriched card data from location state if passed
  const enrichedCard = location.state?.card as EnrichedOwnership | undefined;

  // Detect game from location state OR from the setId prefix in the URL.
  // This ensures direct URL navigation (no location.state) works correctly.
  const isOnePieceSet = (id: string) =>
    /^(OP|EB|ST|PRB)/i.test(id);
  const gameType: 'onepiece' | 'pokemon' =
    enrichedCard?.game ||
    (setId && isOnePieceSet(setId) ? 'onepiece' : 'pokemon');


  // Determine which hooks to use based on game type
  const { data: onePieceOwnership } = useOnePieceCardOwnership(
    gameType === 'onepiece' ? cardId || '' : '',
  );
  const { data: pokemonOwnership } = useCardOwnership(
    gameType === 'pokemon' ? cardId || '' : '',
  );

  const upsertOnePieceOwnership = useUpdateOnePieceCardOwnership();
  const upsertPokemonOwnership = useUpdateCardOwnership();

  const toggleOnePieceMutation = useToggleOnePieceCardOwnership();
  const togglePokemonMutation = useToggleCardOwnership();
  const toggleWishlist = useToggleWishlist();
  const toggleFavorite = useToggleFavorite();

  const ownership =
    gameType === 'onepiece' ? onePieceOwnership : pokemonOwnership;

  // Load card data
  useEffect(() => {
    const loadCard = async () => {
      if (!cardId || !setId) {
        console.warn('[CollectionCardDetail] Missing cardId or setId');
        setLoading(false);
        return;
      }

      // If card data was passed via location state and has an image, use it immediately to avoid API calls
      if (enrichedCard && ((enrichedCard as any).images?.large || (enrichedCard as any).images?.small || (enrichedCard as any).imageUrl)) {
        console.log('[CollectionCardDetail] Using card data from location state:', enrichedCard);
        
        if (gameType === 'onepiece') {
          // If the card already has an images object, it's already been transformed by the service
          if ((enrichedCard as any).images?.large || (enrichedCard as any).images?.small) {
            setCard(enrichedCard as any);
          } else {
            // Otherwise transform it from raw API data
            const transformedCard = transformApiCardToOnePieceCard(enrichedCard as any, setId);
            setCard(transformedCard);
          }
        } else {
          setCard(enrichedCard as any);
        }
        
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('[CollectionCardDetail] Loading card data for:', {
          setId,
          cardId,
          gameType,
        });

        if (gameType === 'onepiece') {
          // Load One Piece card data from API
          const normalizedSetId = setId.toLowerCase();
          const cardsArray = await tcgDataService.getOnePieceSetCards(normalizedSetId);
          
          // Find card
          const foundCard = findOnePieceCard(cardsArray, cardId);

          if (foundCard) {
            // Use the centralized helper to transform the card data properly
            const transformedCard = transformApiCardToOnePieceCard(foundCard, setId);
            setCard(transformedCard);
          }
        } else {
          // Load Pokemon card data from API
          const cardsArray = await tcgDataService.getPokemonSetCards(setId);

          const baseCardId = cardId.split('_')[0];

          const foundCard = (cardsArray as any[]).find((c: any) => {
            const matches =
              c.id === cardId ||
              c.id === baseCardId ||
              c.id?.startsWith(baseCardId);
            return matches;
          });

          if (foundCard) {
            const transformedCard: PokemonCard = {
              id: foundCard.id || '',
              name: foundCard.name || '',
              supertype: foundCard.supertype || 'Pokémon',
              subtypes: foundCard.subtypes || [],
              set: {
                id: foundCard.set?.id || setId,
                name: foundCard.set?.name || setId,
                series: foundCard.set?.series || '',
                printedTotal: foundCard.set?.printedTotal || 0,
                releaseDate: foundCard.set?.releaseDate || '',
              },
              number: foundCard.number || '',
              rarity: foundCard.rarity || 'Common',
              images: {
                small: foundCard.images?.small || '',
                large: foundCard.images?.large || '',
              },
              tcgplayer: foundCard.tcgplayer,
            };
            setCard(transformedCard);
          }
        }
      } catch (error) {
        console.error('[CollectionCardDetail] Error loading card:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [setId, cardId, gameType]);

  const handleSave = async (data: CardOwnershipData) => {
    if (!cardId || !setId) return;

    try {
      if (gameType === 'onepiece') {
        await upsertOnePieceOwnership.mutateAsync({
          cardId,
          data: {
            setId: setId,
            ...data,
          },
        });
      } else {
        await upsertPokemonOwnership.mutateAsync({
          cardId,
          data: {
            setId: setId,
            ...data,
          },
        });
      }
      setShowSnackbar(true);
      setTimeout(() => {
        navigate('/collection');
      }, 1500);
    } catch (error) {
      console.error('Failed to save card ownership:', error);
    }
  };

  const handleToggle = () => {
    if (!cardId || !setId) return;

    if (ownership?.owned) {
      if (gameType === 'onepiece') {
        toggleOnePieceMutation.mutate({
          cardId,
          setId: setId,
        });
      } else {
        togglePokemonMutation.mutate(cardId);
      }
    }
  };

  const enrichedData = enrichedCard ? {
    justtcgId: enrichedCard.justtcgId,
    justtcgName: enrichedCard.justtcgName,
    imageUrl: enrichedCard.imageUrl || (enrichedCard as any).images?.large || (enrichedCard as any).images?.small,
    rarity: enrichedCard.rarity,
    number: enrichedCard.number,
    setName: enrichedCard.setName || (enrichedCard as any).set?.name,
    setSeries: enrichedCard.setSeries || (enrichedCard as any).set?.series,
  } : undefined;

  const breadcrumbs = [
    { label: 'Collection', onClick: () => navigate('/collection') },
  ];

  return (
    <CardDetailView
      card={card}
      loading={loading}
      gameType={gameType} // 'pokemon' or 'onepiece'
      setId={setId || ''}
      cardId={cardId || ''}
      onBack={() => navigate(-1)}
      breadcrumbs={breadcrumbs}
      ownership={ownership}
      onToggleOwnership={handleToggle}
      onToggleWishlist={() => {
        if (!cardId || !setId || !card) return;
        toggleWishlist.mutate({
          game: gameType,
          setId,
          cardId: gameType === 'onepiece' ? ((card as any).baseCardId || cardId) : cardId,
          isWishlist: !ownership?.isWishlist,
          cardName: (card as any).justtcgName || card.name,
          imageUrl: card.images.small || card.images.large,
          rarity: card.rarity,
          color: (card as any).color,
          types: (card as any).types,
          cardNumber: (card as any).number,
          baseCardId: (card as any).baseCardId || card.id,
          imageId: cardId
        });
      }}
      onToggleFavorite={() => {
        if (!cardId || !setId || !card) return;
        toggleFavorite.mutate({
          game: gameType,
          setId,
          cardId: gameType === 'onepiece' ? ((card as any).baseCardId || cardId) : cardId,
          isFavorite: !ownership?.isFavorite,
          cardName: (card as any).justtcgName || card.name,
          imageUrl: card.images.small || card.images.large,
          rarity: card.rarity,
          color: (card as any).color,
          types: (card as any).types,
          cardNumber: (card as any).number,
          baseCardId: (card as any).baseCardId || card.id,
          imageId: cardId
        });
      }}
      onSaveOwnership={handleSave}
      isSaving={upsertOnePieceOwnership.isPending || upsertPokemonOwnership.isPending}
      isToggling={
        toggleWishlist.isPending || toggleFavorite.isPending
      }
      showSnackbar={showSnackbar}
      onCloseSnackbar={() => setShowSnackbar(false)}
      enrichedData={enrichedData}
    />
  );
};

export default CollectionCardDetail;
